import type {
  TicketDetail,
  TicketListScope,
  TicketPriority,
  TicketQueueFilters,
  TicketQueuePage,
  TicketQueueQueryInput,
} from '@easyhelp/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './client';

/**
 * Radice delle chiavi dei ticket: chi modifica un ticket (#3 priorità, #4 assegnazione, #5
 * thread) invalida `ticketsQueryKey` e coda e dettaglio si riallineano insieme.
 */
export const ticketsQueryKey = ['tickets'] as const;

/** Chiave di una pagina della coda con quei filtri. */
export const ticketQueueQueryKey = (query: TicketQueueQueryInput) => [...ticketsQueryKey, 'queue', query] as const;

/** Chiave del dettaglio di un ticket. */
export const ticketQueryKey = (code: string) => [...ticketsQueryKey, 'detail', code] as const;

/** Query string di `GET /tickets` con i soli parametri valorizzati. */
export function ticketQueueSearch(query: TicketQueueQueryInput): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `?${search}` : '';
}

/** Ogni minuto la coda si riallinea: arrivano ticket nuovi e il tempo trascorso cambia. */
const QUEUE_REFRESH_MS = 60_000;

/**
 * Margine del badge oltre il minuto della coda: la coda senza filtri riscrive il badge un attimo
 * dopo il proprio minuto (il tempo della risposta), e il badge non deve precederla con una
 * richiesta sua. Lontano dalla coda il badge si rilegge comunque, una volta ogni 70 secondi.
 */
const OPEN_COUNT_GRACE_MS = 10_000;

/**
 * Chiave dei contatori di tutta la coda o di tutto l'archivio (`counts` senza filtri né ricerca): il
 * badge della voce «Coda» (#104) e i sottotitoli «N ticket aperti» e «N ticket in archivio» (#123).
 * Sta sotto `ticketsQueryKey`, così ogni modifica a un ticket la riallinea.
 */
export const queueTotalsQueryKey = (scope: TicketListScope = 'coda') => [...ticketsQueryKey, 'totals', scope] as const;

/**
 * Filtri e ricerca: sono i soli parametri da cui dipendono i contatori delle viste (`TicketQueuePage.counts`).
 * Un oggetto con tutte le chiavi di `TicketQueueFilters`, così un filtro nuovo non compila finché non è qui.
 */
const COUNT_FILTERS = Object.keys({
  committente: true,
  priority: true,
  state: true,
  assignee: true,
  reply: true,
  q: true,
} satisfies Record<keyof TicketQueueFilters, true>) as (keyof TicketQueueFilters)[];

/**
 * Vero se i contatori della pagina valgono per tutto l'ambito: né filtri né ricerca. Vista,
 * ordinamento e paginazione non li cambiano (l'API li calcola solo con filtri e ricerca).
 */
export function countsCoverWholeQueue(query: TicketQueueQueryInput): boolean {
  return COUNT_FILTERS.every((key) => !query[key]?.trim());
}

/**
 * `GET /tickets`: una pagina della coda o dell'archivio (`scope`, #123) con i contatori delle viste.
 * Cambiando filtro la pagina precedente resta visibile finché arriva la nuova, così la lista non
 * lampeggia. Senza filtri né ricerca i suoi `counts` valgono per tutto l'ambito: li scrive in
 * `queueTotalsQueryKey`, così finché la lista è aperta badge e sottotitolo non fanno richieste loro.
 */
export function useTicketQueue(query: TicketQueueQueryInput) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ticketQueueQueryKey(query),
    queryFn: async () => {
      const page = await apiRequest<TicketQueuePage>(`/tickets${ticketQueueSearch(query)}`);
      if (countsCoverWholeQueue(query)) queryClient.setQueryData(queueTotalsQueryKey(query.scope), page.counts);
      return page;
    },
    placeholderData: keepPreviousData,
    refetchInterval: QUEUE_REFRESH_MS,
  });
}

/** `tutti` è tutto l'ambito: gli aperti per la coda, gli archiviati per l'archivio. Stabile: react-query non lo riesegue a ogni render. */
const selectScopeTotal = (counts: TicketQueuePage['counts']) => counts.tutti ?? 0;

/**
 * Ticket di tutta la coda o di tutto l'archivio, per il sottotitolo della pagina («N ticket aperti» o
 * «N ticket in archivio», #123) e per il badge della Coda: non dipendono da vista, filtri e ricerca.
 * Una pagina di un solo ticket senza filtri basta a leggere `counts`, senza un endpoint apposta.
 * `enabled: false` quando la pagina corrente li porta già (nessun filtro né ricerca).
 *
 * Freschi per un minuto, come la coda. `refetchInterval` di react-query rilegge anche un dato fresco
 * e riparte da capo a ogni aggiornamento del dato: per questo l'intervallo è un poco più lungo di
 * quello della coda (`OPEN_COUNT_GRACE_MS`). Con la coda senza filtri aperta, ogni sua lettura
 * riscrive i contatori prima che scadano e la richiesta non parte.
 */
export function useQueueTotals(scope: TicketListScope, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queueTotalsQueryKey(scope),
    queryFn: async () =>
      (await apiRequest<TicketQueuePage>(`/tickets${ticketQueueSearch({ scope, pageSize: 1 })}`)).counts,
    staleTime: QUEUE_REFRESH_MS,
    refetchInterval: QUEUE_REFRESH_MS + OPEN_COUNT_GRACE_MS,
    select: selectScopeTotal,
    enabled,
  });
}

/**
 * Ticket aperti (Nuovo, In lavorazione, In attesa dell'utente), cioè tutta la coda, per il badge
 * della voce «Coda» (#104): con l'archivio (#123) il numero non cambia.
 */
export function useOpenTicketCount() {
  return useQueueTotals('coda');
}

/** `GET /tickets/:code`: il dettaglio in sola lettura. */
export function useTicket(code: string) {
  return useQuery({
    queryKey: ticketQueryKey(code),
    queryFn: () => apiRequest<TicketDetail>(`/tickets/${encodeURIComponent(code)}`),
  });
}

/**
 * `PUT /tickets/:code/priority` (#3): imposta o cambia la priorità. La risposta è il dettaglio
 * aggiornato, con l'evento nel thread, e prende il posto del dettaglio in cache; il resto della
 * radice `['tickets']` è invalidato, così coda, contatori delle viste e filtri si riallineano senza
 * rileggere il dettaglio appena ricevuto.
 */
export function useSetTicketPriority(code: string) {
  const queryClient = useQueryClient();
  return useMutation<TicketDetail, ApiError, TicketPriority>({
    mutationFn: (priority) =>
      apiRequest<TicketDetail>(`/tickets/${encodeURIComponent(code)}/priority`, {
        method: 'PUT',
        body: JSON.stringify({ priority }),
      }),
    onSuccess: async (detail) => {
      const detailKey = ticketQueryKey(code);
      queryClient.setQueryData(detailKey, detail);
      await queryClient.invalidateQueries({
        queryKey: ticketsQueryKey,
        predicate: (query) => !detailKey.every((part, index) => query.queryKey[index] === part),
      });
    },
  });
}
